import React, { useState, useRef, useEffect } from "react";
import AppFooter from "./AppFooter";
import { supabase } from "./supabaseClient";

// Elérhető méretek konfigurációja
const AVAILABLE_SIZES = [
  {
    id: "63.5x63.5",
    name: "Négyzet alakú fotómágnes",
    dimensions: "63,5 × 63,5 mm",
    aspectRatio: 1, // 1:1
    unitPrice: 990,
    description: "Klasszikus prémium hűtőmágnes, éles nyomat, tartós felület.",
    badge: "Alapméret",
    available: true,
  },
  {
    id: "90x60",
    name: "Fekvő téglalap fotómágnes",
    dimensions: "90 × 60 mm",
    aspectRatio: 1.5,
    unitPrice: 1290,
    description: "Panoráma és fekvő formátumú képekhez.",
    badge: "Hamarosan",
    available: false,
  },
  {
    id: "50x50",
    name: "Mini négyzetes mágnes",
    dimensions: "50 × 50 mm",
    aspectRatio: 1,
    unitPrice: 790,
    description: "Kompakt méret, ideális képekből álló kollázshoz.",
    badge: "Hamarosan",
    available: false,
  },
];

export default function MagnetOrder({ onBack, user, isLandscape }) {
  // Lépések: 1: Méret & Kép, 2: Darabszám & Ár, 3: Cím & Fizetés, 4: Siker
  const [step, setStep] = useState(1);

  // Méret választás
  const [selectedSizeId, setSelectedSizeId] = useState("63.5x63.5");
  const currentSize = AVAILABLE_SIZES.find((s) => s.id === selectedSizeId) || AVAILABLE_SIZES[0];

  // 1. Kép állapota
  const [imageSrc, setImageSrc] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [imageFitScale, setImageFitScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [croppedImage, setCroppedImage] = useState(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);

  // 2. Darabszám & Ár
  const [quantity, setQuantity] = useState(1);
  const unitPrice = currentSize.unitPrice;

  const getDiscount = (qty) => {
    if (qty >= 20) return 0.25;
    if (qty >= 10) return 0.15;
    if (qty >= 5) return 0.1;
    return 0;
  };
  const discountRate = getDiscount(quantity);
  const rawSubtotal = quantity * unitPrice;
  const subtotal = Math.round(rawSubtotal * (1 - discountRate));

  // 3. Szállítási és Számlázási adatok (külön Családnév és Keresztnév)
  const [shippingMethod, setShippingMethod] = useState("gls_home");

  const [formData, setFormData] = useState({
    lastName: "",
    firstName: "",
    email: user?.email || "",
    phone: "",
    // Szállítási cím
    zip: "",
    city: "",
    street: "",
    comment: "",
    // Számlázási cím
    sameAsBilling: true,
    billingLastName: "",
    billingFirstName: "",
    billingTaxNumber: "",
    billingZip: "",
    billingCity: "",
    billingStreet: "",
    // Jogi elfogadások
    acceptTerms: false,
    acceptGdpr: false,
  });

  const [formErrors, setFormErrors] = useState({});
  const [checkoutMessage, setCheckoutMessage] = useState("");
  const [orderNumber, setOrderNumber] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const containerRef = useRef(null);
  const imgRef = useRef(null);

  const getShippingCost = () => {
    if (shippingMethod === "personal") return 0;
    if (shippingMethod === "foxpost_box") return 990;
    return 1490;
  };
  const shippingCost = getShippingCost();
  const grandTotal = subtotal + shippingCost;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const paymentState = params.get("magnet_payment");

    if (paymentState === "cancelled") {
      setCheckoutMessage("A fizetés megszakadt. A rendelés még nincs kifizetve; próbáld újra.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sessionId) return;

    let isActive = true;
    async function verifyCheckout() {
      try {
        const response = await fetch(
          `https://nyelvtanuloappbackend.onrender.com/magnet-checkout-status?session_id=${encodeURIComponent(sessionId)}`
        );
        const result = await response.json();
        if (!response.ok || !result.paid) {
          throw new Error(result.error || "A fizetés még nem igazolható.");
        }
        if (isActive) {
          setOrderNumber(result.orderNumber);
          setCheckoutMessage("");
          setStep(4);
        }
      } catch (error) {
        if (isActive) {
          setCheckoutMessage("A fizetést nem sikerült ellenőrizni. Kérjük, nézd meg a Stripe visszaigazolását, és ne indíts új fizetést.");
        }
      } finally {
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
    verifyCheckout();
    return () => { isActive = false; };
  }, []);

  // Képfájl betöltése
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setZoom(1);
        setImageFitScale(1);
        setPan({ x: 0, y: 0 });
        setCroppedImage(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageLoad = () => {
    const img = imgRef.current;
    const container = containerRef.current;
    if (!img || !container || !img.naturalWidth || !img.naturalHeight) return;

    setImageFitScale(Math.max(
      container.clientWidth / img.naturalWidth,
      container.clientHeight / img.naturalHeight
    ));
  };

  useEffect(() => {
    if (!croppedImage) {
      setCroppedImageUrl(null);
      return;
    }

    const previewUrl = URL.createObjectURL(croppedImage);
    setCroppedImageUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [croppedImage]);

  // Drag & Move kezelése
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - pan.x,
        y: e.touches[0].clientY - pan.y,
      });
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    setPan({
      x: e.touches[0].clientX - dragStart.x,
      y: e.touches[0].clientY - dragStart.y,
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.0015;
    setZoom((prev) => Math.min(Math.max(0.5, prev + delta), 3.5));
  };

  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDragging) {
        setPan({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    };
    const handleGlobalMouseUp = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener("mousemove", handleGlobalMouseMove);
      window.addEventListener("mouseup", handleGlobalMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, dragStart]);

  // Vágott kép generálása
  const generateCroppedImage = () => {
    if (!imageSrc || !imgRef.current || !containerRef.current) return;
    const canvas = document.createElement("canvas");
    const img = imgRef.current;
    const container = containerRef.current;
    const displayScale = imageFitScale * zoom;
    if (!displayScale || !img.naturalWidth || !img.naturalHeight) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    canvas.width = Math.max(1, Math.round(containerWidth / displayScale));
    canvas.height = Math.max(1, Math.round(containerHeight / displayScale));
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setCheckoutMessage("A képet nem sikerült előkészíteni. Próbáld meg újra.");
      return;
    }

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const renderedWidth = img.naturalWidth * displayScale;
    const renderedHeight = img.naturalHeight * displayScale;
    const drawX = ((containerWidth - renderedWidth) / 2 + pan.x) * canvas.width / containerWidth;
    const drawY = ((containerHeight - renderedHeight) / 2 + pan.y) * canvas.height / containerHeight;
    const drawW = renderedWidth * canvas.width / containerWidth;
    const drawH = renderedHeight * canvas.height / containerHeight;

    ctx.drawImage(img, drawX, drawY, drawW, drawH);

    canvas.toBlob((blob) => {
      if (!blob) {
        setCheckoutMessage("A képet nem sikerült előkészíteni. Próbáld meg újra.");
        return;
      }
      setCroppedImage(blob);
      setStep(2);
    }, "image/jpeg", 1);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Validálás, függő rendelés mentése, majd Stripe Checkout indítása
  const validateAndSubmit = async () => {
    setCheckoutMessage("");
    const errors = {};
    if (!formData.lastName.trim()) errors.lastName = "Családnév kötelező!";
    if (!formData.firstName.trim()) errors.firstName = "Keresztnév kötelező!";
    if (!formData.email.trim() || !formData.email.includes("@")) errors.email = "Érvényes e-mail cím szükséges!";
    if (!formData.phone.trim() || formData.phone.length < 8) errors.phone = "Telefonszám kötelező a futárhoz!";
    if (!formData.zip.trim()) errors.zip = "Irányítószám kötelező!";
    if (!formData.city.trim()) errors.city = "Település kötelező!";
    if (!formData.street.trim()) errors.street = "Utca, házszám kötelező!";

    if (!formData.sameAsBilling) {
      if (!formData.billingLastName.trim()) errors.billingLastName = "Számlázási név / Cégnév kötelező!";
      if (!formData.billingZip.trim()) errors.billingZip = "Számlázási irányítószám kötelező!";
      if (!formData.billingCity.trim()) errors.billingCity = "Számlázási település kötelező!";
      if (!formData.billingStreet.trim()) errors.billingStreet = "Számlázási utca kötelező!";
    }

    if (!formData.acceptTerms) errors.acceptTerms = "Az ÁSZF elfogadása kötelező!";
    if (!formData.acceptGdpr) errors.acceptGdpr = "Az Adatkezelési tájékoztató elfogadása kötelező!";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    const generatedId = `MGN-${crypto.randomUUID()}`;

    const fullName = `${formData.lastName.trim()} ${formData.firstName.trim()}`;
    const billingFullName = formData.sameAsBilling
      ? fullName
      : `${formData.billingLastName.trim()} ${formData.billingFirstName.trim()}`.trim();

    try {
      let finalImageUrl;

      if (!croppedImage || !supabase) {
        throw new Error("A képfeltöltéshez szükséges adat hiányzik.");
      }
      const fileName = `${generatedId}.jpg`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("magnet-images")
        .upload(fileName, croppedImage, { contentType: "image/jpeg", upsert: false });
      if (uploadError || !uploadData) {
        throw new Error(`A képet nem sikerült feltölteni: ${uploadError?.message || "ismeretlen Storage-hiba"}`);
      }
      finalImageUrl = fileName;

      // 2. Mentés Supabase adatbázisba
      const orderPayload = {
        order_number: generatedId,
        user_id: user?.id || null,
        size_id: currentSize.id,
        size_name: currentSize.dimensions,
        quantity: quantity,
        unit_price: unitPrice,
        subtotal: subtotal,
        shipping_cost: shippingCost,
        total_price: grandTotal,
        payment_method: "stripe",
        shipping_method: shippingMethod,
        last_name: formData.lastName,
        first_name: formData.firstName,
        full_name: fullName,
        email: formData.email,
        phone: formData.phone,
        shipping_zip: formData.zip,
        shipping_city: formData.city,
        shipping_street: formData.street,
        shipping_comment: formData.comment || null,
        billing_same: formData.sameAsBilling,
        billing_name: billingFullName,
        billing_tax_number: formData.billingTaxNumber || null,
        billing_zip: formData.sameAsBilling ? formData.zip : formData.billingZip,
        billing_city: formData.sameAsBilling ? formData.city : formData.billingCity,
        billing_street: formData.sameAsBilling ? formData.street : formData.billingStreet,
        image_url: finalImageUrl,
        status: "uj",
        created_at: new Date().toISOString(),
      };

      const { error: orderError } = await supabase.from("magnet_orders").insert([orderPayload]);
      if (orderError) {
        throw new Error("A rendelési adatokat nem sikerült elmenteni: " + orderError.message);
      }

      const response = await fetch("https://nyelvtanuloappbackend.onrender.com/create-magnet-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderNumber: generatedId }),
      });
      const checkout = await response.json();
      if (!response.ok || !checkout.url) {
        throw new Error(checkout.error || "Nem sikerült elindítani a Stripe-fizetést.");
      }
      window.location.assign(checkout.url);
    } catch (err) {
      console.warn("Order submission error:", err);
      setCheckoutMessage(err.message || "Hiba történt a rendelés indításakor.");
      setIsSubmitting(false);
    }
  };

  // MLBB vs normál stílusok
  const cardStyle = {
    width: "100%",
    maxWidth: isLandscape ? 560 : 460,
    background: isLandscape ? "rgba(20, 25, 45, 0.90)" : "#fff",
    backdropFilter: isLandscape ? "blur(12px)" : "none",
    borderRadius: 24,
    boxShadow: isLandscape
      ? "0 0 30px rgba(255, 204, 0, 0.4), 0 8px 32px rgba(0, 0, 0, 0.7)"
      : "0 8px 32px rgba(44,62,80,0.11)",
    border: isLandscape ? "2px solid #ffcc00" : "1px solid #222",
    padding: isLandscape ? "24px 28px" : "28px 24px",
    boxSizing: "border-box",
    color: isLandscape ? "#fff" : "#2d3a4a",
  };

  const titleStyle = {
    margin: "0 0 16px 0",
    color: isLandscape ? "#ffcc00" : "#2d3a4a",
    fontSize: "1.35rem",
    textAlign: "center",
    fontWeight: 800,
    textShadow: isLandscape ? "0 0 16px rgba(255, 204, 0, 0.7)" : "none",
  };

  const inputStyle = (hasError) => ({
    width: "100%",
    padding: "9px 12px",
    borderRadius: 10,
    border: hasError ? "2px solid #ef4444" : isLandscape ? "1px solid #4f8cff" : "1px solid #cbd5e1",
    background: isLandscape ? "rgba(10, 15, 35, 0.8)" : "#fff",
    color: isLandscape ? "#fff" : "#2d3a4a",
    boxSizing: "border-box",
    fontSize: "0.92rem",
    outline: "none",
  });

  const nextBtnStyle = {
    background: isLandscape
      ? "linear-gradient(90deg, #ff4081 0%, #ff6b35 100%)"
      : "linear-gradient(90deg, #66a6ff 0%, #89f7fe 100%)",
    border: isLandscape ? "2px solid #ff4081" : "none",
    boxShadow: isLandscape
      ? "0 0 20px rgba(255, 64, 129, 0.6), 0 4px 12px rgba(0, 0, 0, 0.4)"
      : "0 4px 14px rgba(102,166,255,0.4)",
    color: "#fff",
    borderRadius: 16,
    padding: "12px 24px",
    fontSize: "1rem",
    fontWeight: 800,
    cursor: "pointer",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: isLandscape
          ? "linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)"
          : "#f7f9fc",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* Navigációs sáv */}
      <header
        style={{
          background: isLandscape ? "rgba(15, 20, 40, 0.95)" : "#fff",
          borderBottom: isLandscape ? "1px solid #ffcc00" : "1px solid #e2e8f0",
          padding: "12px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: isLandscape ? "0 0 15px rgba(255, 204, 0, 0.2)" : "0 2px 6px rgba(0,0,0,0.04)",
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: isLandscape ? "rgba(30, 35, 60, 0.8)" : "#edf2f7",
            border: isLandscape ? "1px solid #4f8cff" : "1px solid #cbd5e1",
            borderRadius: 12,
            padding: "8px 16px",
            cursor: "pointer",
            fontWeight: 700,
            color: isLandscape ? "#4f8cff" : "#2d3a4a",
          }}
        >
          ← Főmenü
        </button>

        <h2 style={{ margin: 0, fontSize: "1.15rem", color: isLandscape ? "#ffcc00" : "#2d3a4a", fontWeight: 800 }}>
          🧲 Mágnes Rendelés
        </h2>

        {/* Lépések */}
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { num: 1, label: "Kép & Méret" },
            { num: 2, label: "Darabszám" },
            { num: 3, label: "Adatok" },
            { num: 4, label: "Kész" },
          ].map((item) => (
            <div
              key={item.num}
              style={{
                padding: "4px 10px",
                borderRadius: 12,
                background: step === item.num ? (isLandscape ? "#ff4081" : "#4f8cff") : step > item.num ? "#10b981" : (isLandscape ? "rgba(255,255,255,0.1)" : "#f1f5f9"),
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.8rem",
              }}
            >
              {step > item.num ? "✓" : item.num}
            </div>
          ))}
        </div>
      </header>

      {/* Fő tartalom */}
      <main style={{ flex: 1, padding: "24px 16px", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
        <div style={cardStyle}>
          {/* ==================== 1. LÉPÉS: MÉRETVÁLASZTÁS & KÉPVÁGÁS ==================== */}
          {step === 1 && (
            <div>
              <h3 style={titleStyle}>1. Méret és Kép kiválasztása</h3>

              {/* Méretválasztó */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: isLandscape ? "#ffcc00" : "#475569", display: "block", marginBottom: 8 }}>
                  📏 Válassz mágnes méretet:
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {AVAILABLE_SIZES.map((size) => (
                    <div
                      key={size.id}
                      onClick={() => size.available && setSelectedSizeId(size.id)}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 14,
                        border: selectedSizeId === size.id ? (isLandscape ? "2px solid #ffcc00" : "2px solid #4f8cff") : "1px solid rgba(255,255,255,0.15)",
                        background: selectedSizeId === size.id ? (isLandscape ? "rgba(255,204,0,0.12)" : "#f0f6ff") : (isLandscape ? "rgba(15,20,40,0.6)" : "#fff"),
                        cursor: size.available ? "pointer" : "not-allowed",
                        opacity: size.available ? 1 : 0.6,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, color: isLandscape ? "#fff" : "#2d3a4a", fontSize: "0.92rem" }}>
                          {size.dimensions} – {size.name}
                        </div>
                        <div style={{ fontSize: "0.78rem", color: isLandscape ? "#94a3b8" : "#64748b" }}>{size.description}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            padding: "2px 7px",
                            borderRadius: 6,
                            background: size.available ? (isLandscape ? "#ff4081" : "#dbeafe") : "#64748b",
                            color: size.available ? "#fff" : "#cbd5e1",
                          }}
                        >
                          {size.badge}
                        </span>
                        <div style={{ fontWeight: 800, color: isLandscape ? "#ffcc00" : "#2d3a4a", marginTop: 3, fontSize: "0.88rem" }}>
                          {size.unitPrice} Ft / db
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Képfeltöltés vagy Facebook-stílusú vágó */}
              {!imageSrc ? (
                <label
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: isLandscape ? "2px dashed #ffcc00" : "2px dashed #4f8cff",
                    borderRadius: 18,
                    padding: "36px 16px",
                    cursor: "pointer",
                    background: isLandscape ? "rgba(255,204,0,0.05)" : "#f8fbff",
                    textAlign: "center",
                  }}
                >
                  <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: "none" }} />
                  <div style={{ fontSize: "2.8rem", marginBottom: 8 }}>📷</div>
                  <div style={{ fontSize: "1.05rem", fontWeight: 700, color: isLandscape ? "#fff" : "#2d3a4a", marginBottom: 4 }}>
                    Kattints ide a kép feltöltéséhez
                  </div>
                  <div style={{ fontSize: "0.8rem", color: isLandscape ? "#94a3b8" : "#64748b" }}>
                    Beillesztés a(z) {currentSize.dimensions}-es keretbe
                  </div>
                </label>
              ) : (
                <div>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
                    <div
                      ref={containerRef}
                      onMouseDown={handleMouseDown}
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      onWheel={handleWheel}
                      style={{
                        width: 280,
                        height: 280,
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 12,
                        boxShadow: isLandscape
                          ? "0 0 20px rgba(255,204,0,0.5), 0 0 0 2px #ffcc00"
                          : "0 4px 16px rgba(0,0,0,0.2), 0 0 0 2px #4f8cff",
                        cursor: isDragging ? "grabbing" : "grab",
                        userSelect: "none",
                        touchAction: "none",
                        background: "#0f172a",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        ref={imgRef}
                        src={imageSrc}
                        onLoad={handleImageLoad}
                        alt="Feltöltött fotó"
                        draggable={false}
                        style={{
                          position: "absolute",
                          transform: `translate(${pan.x}px, ${pan.y}px) scale(${imageFitScale * zoom})`,
                          transformOrigin: "center center",
                          maxWidth: "none",
                          maxHeight: "none",
                          pointerEvents: "none",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          border: "1px solid rgba(255,255,255,0.4)",
                          pointerEvents: "none",
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gridTemplateRows: "1fr 1fr 1fr",
                        }}
                      >
                        <div style={{ borderRight: "1px dashed rgba(255,255,255,0.25)", borderBottom: "1px dashed rgba(255,255,255,0.25)" }} />
                        <div style={{ borderRight: "1px dashed rgba(255,255,255,0.25)", borderBottom: "1px dashed rgba(255,255,255,0.25)" }} />
                        <div style={{ borderBottom: "1px dashed rgba(255,255,255,0.25)" }} />
                        <div style={{ borderRight: "1px dashed rgba(255,255,255,0.25)", borderBottom: "1px dashed rgba(255,255,255,0.25)" }} />
                        <div style={{ borderRight: "1px dashed rgba(255,255,255,0.25)", borderBottom: "1px dashed rgba(255,255,255,0.25)" }} />
                        <div style={{ borderBottom: "1px dashed rgba(255,255,255,0.25)" }} />
                        <div style={{ borderRight: "1px dashed rgba(255,255,255,0.25)" }} />
                        <div style={{ borderRight: "1px dashed rgba(255,255,255,0.25)" }} />
                        <div />
                      </div>

                      <div
                        style={{
                          position: "absolute",
                          bottom: 6,
                          right: 6,
                          background: "rgba(0,0,0,0.75)",
                          color: "#ffcc00",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          padding: "2px 7px",
                          borderRadius: 4,
                          pointerEvents: "none",
                        }}
                      >
                        {currentSize.dimensions}
                      </div>
                    </div>
                  </div>

                  {/* Nagyítás csúszka */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 16 }}>
                    <button
                      onClick={() => setZoom((prev) => Math.max(0.5, prev - 0.1))}
                      style={{ width: 34, height: 34, borderRadius: "50%", border: "1px solid #4f8cff", background: isLandscape ? "rgba(30,35,60,0.8)" : "#fff", color: isLandscape ? "#fff" : "#2d3a4a", cursor: "pointer", fontWeight: 700 }}
                    >
                      −
                    </button>
                    <input
                      type="range"
                      min="0.5"
                      max="3.0"
                      step="0.05"
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      style={{ width: 150, cursor: "pointer" }}
                    />
                    <button
                      onClick={() => setZoom((prev) => Math.min(3.0, prev + 0.1))}
                      style={{ width: 34, height: 34, borderRadius: "50%", border: "1px solid #4f8cff", background: isLandscape ? "rgba(30,35,60,0.8)" : "#fff", color: isLandscape ? "#fff" : "#2d3a4a", cursor: "pointer", fontWeight: 700 }}
                    >
                      +
                    </button>
                  </div>

                  <div style={{ display: "flex", gap: 10, justifyContent: "space-between" }}>
                    <label
                      style={{
                        background: isLandscape ? "rgba(30,35,60,0.8)" : "#f1f5f9",
                        border: isLandscape ? "1px solid #4f8cff" : "1px solid #cbd5e1",
                        borderRadius: 14,
                        padding: "10px 14px",
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: isLandscape ? "#fff" : "#475569",
                        cursor: "pointer",
                      }}
                    >
                      <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: "none" }} />
                      🔄 Másik kép
                    </label>

                    <button onClick={generateCroppedImage} style={nextBtnStyle}>
                      Tovább ➔
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ==================== 2. LÉPÉS: DARABSZÁM & ÁR ==================== */}
          {step === 2 && (
            <div>
              <h3 style={titleStyle}>2. Mennyiség és Ár</h3>

              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <img
                  src={croppedImageUrl}
                  alt="Előnézet"
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: 10,
                    border: isLandscape ? "2px solid #ffcc00" : "2px solid #e2e8f0",
                    boxShadow: isLandscape ? "0 0 20px rgba(255,204,0,0.4)" : "0 4px 12px rgba(0,0,0,0.1)",
                    display: "inline-block",
                  }}
                />
                <div style={{ fontSize: "0.85rem", color: isLandscape ? "#ffcc00" : "#64748b", marginTop: 4, fontWeight: 700 }}>
                  Méret: {currentSize.dimensions}
                </div>
              </div>

              {/* Darabszám számláló */}
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <button
                  onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                  style={{ width: 42, height: 42, borderRadius: 12, border: isLandscape ? "1px solid #ffcc00" : "1px solid #cbd5e1", background: isLandscape ? "#1e233c" : "#fff", color: isLandscape ? "#fff" : "#2d3a4a", fontSize: "1.3rem", fontWeight: 700, cursor: "pointer" }}
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.min(500, Math.max(1, parseInt(e.target.value) || 1)))}
                  style={{ width: 65, height: 42, textAlign: "center", fontSize: "1.25rem", fontWeight: 800, borderRadius: 12, border: isLandscape ? "1px solid #ffcc00" : "1px solid #cbd5e1", background: isLandscape ? "#0a0e27" : "#fff", color: isLandscape ? "#ffcc00" : "#2d3a4a" }}
                />
                <button
                  onClick={() => setQuantity((prev) => Math.min(500, prev + 1))}
                  style={{ width: 42, height: 42, borderRadius: 12, border: isLandscape ? "1px solid #ffcc00" : "1px solid #cbd5e1", background: isLandscape ? "#1e233c" : "#fff", color: isLandscape ? "#fff" : "#2d3a4a", fontSize: "1.3rem", fontWeight: 700, cursor: "pointer" }}
                >
                  +
                </button>
                <span style={{ fontWeight: 700, color: isLandscape ? "#fff" : "#2d3a4a" }}>db</span>
              </div>

              {/* Gyorsválasztó gombok */}
              <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 20 }}>
                {[1, 3, 5, 10, 20].map((num) => (
                  <button
                    key={num}
                    onClick={() => setQuantity(num)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: quantity === num ? (isLandscape ? "2px solid #ffcc00" : "2px solid #4f8cff") : "1px solid rgba(255,255,255,0.2)",
                      background: quantity === num ? (isLandscape ? "rgba(255,204,0,0.2)" : "#eff6ff") : (isLandscape ? "rgba(20,25,45,0.7)" : "#fff"),
                      color: quantity === num ? (isLandscape ? "#ffcc00" : "#2563eb") : (isLandscape ? "#fff" : "#475569"),
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      cursor: "pointer",
                    }}
                  >
                    {num} db
                  </button>
                ))}
              </div>

              {/* Ár kártya */}
              <div style={{ background: isLandscape ? "rgba(10,15,35,0.7)" : "#f8fafc", padding: 14, borderRadius: 14, border: isLandscape ? "1px solid rgba(255,204,0,0.3)" : "1px solid #e2e8f0", marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", color: isLandscape ? "#94a3b8" : "#64748b", marginBottom: 4 }}>
                  <span>Egységár:</span>
                  <span>{unitPrice} Ft / db</span>
                </div>
                {discountRate > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", color: "#10b981", fontWeight: 700, marginBottom: 4 }}>
                    <span>Kedvezmény ({discountRate * 100}%):</span>
                    <span>- {Math.round(rawSubtotal * discountRate)} Ft</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.15rem", fontWeight: 800, color: isLandscape ? "#ffcc00" : "#2d3a4a", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 8 }}>
                  <span>Mágnesek összege:</span>
                  <span style={{ color: isLandscape ? "#ffcc00" : "#2563eb" }}>{subtotal.toLocaleString()} Ft</span>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  onClick={() => setStep(1)}
                  style={{ background: isLandscape ? "rgba(30,35,60,0.8)" : "#f1f5f9", border: isLandscape ? "1px solid #4f8cff" : "1px solid #cbd5e1", borderRadius: 14, padding: "10px 16px", fontWeight: 600, color: isLandscape ? "#fff" : "#475569", cursor: "pointer" }}
                >
                  ← Vissza
                </button>
                <button onClick={() => setStep(3)} style={nextBtnStyle}>
                  Tovább a címekhez ➔
                </button>
              </div>
            </div>
          )}

          {/* ==================== 3. LÉPÉS: CÍMEK, ADATOK, KÜLÖN VEZETÉKNÉV & KERESZTNÉV ==================== */}
          {step === 3 && (
            <div>
              <h3 style={titleStyle}>3. Szállítási és Számlázási Címek</h3>

              {/* Családnév és Keresztnév KÜLÖN */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: "0.8rem", fontWeight: 700, color: isLandscape ? "#ffcc00" : "#475569", display: "block", marginBottom: 3 }}>
                      Családnév (Vezetéknév) *
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange("lastName", e.target.value)}
                      placeholder="pl. Kovács"
                      style={inputStyle(formErrors.lastName)}
                    />
                    {formErrors.lastName && <span style={{ color: "#ef4444", fontSize: "0.7rem" }}>{formErrors.lastName}</span>}
                  </div>

                  <div>
                    <label style={{ fontSize: "0.8rem", fontWeight: 700, color: isLandscape ? "#ffcc00" : "#475569", display: "block", marginBottom: 3 }}>
                      Keresztnév (Utónév) *
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange("firstName", e.target.value)}
                      placeholder="pl. Péter"
                      style={inputStyle(formErrors.firstName)}
                    />
                    {formErrors.firstName && <span style={{ color: "#ef4444", fontSize: "0.7rem" }}>{formErrors.firstName}</span>}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: "0.8rem", fontWeight: 700, color: isLandscape ? "#ffcc00" : "#475569", display: "block", marginBottom: 3 }}>
                      E-mail cím *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      placeholder="peter@pelda.hu"
                      style={inputStyle(formErrors.email)}
                    />
                    {formErrors.email && <span style={{ color: "#ef4444", fontSize: "0.7rem" }}>{formErrors.email}</span>}
                  </div>
                  <div>
                    <label style={{ fontSize: "0.8rem", fontWeight: 700, color: isLandscape ? "#ffcc00" : "#475569", display: "block", marginBottom: 3 }}>
                      Telefonszám (futárhoz) *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      placeholder="+36 30 123 4567"
                      style={inputStyle(formErrors.phone)}
                    />
                    {formErrors.phone && <span style={{ color: "#ef4444", fontSize: "0.7rem" }}>{formErrors.phone}</span>}
                  </div>
                </div>
              </div>

              {/* Szállítási Cím */}
              <div style={{ background: isLandscape ? "rgba(10,15,35,0.7)" : "#f8fafc", padding: 12, borderRadius: 12, border: isLandscape ? "1px solid rgba(255,204,0,0.3)" : "1px solid #e2e8f0", marginBottom: 14 }}>
                <span style={{ fontSize: "0.85rem", fontWeight: 800, color: isLandscape ? "#ffcc00" : "#2d3a4a", display: "block", marginBottom: 8 }}>
                  📍 Szállítási Cím
                </span>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8, marginBottom: 8 }}>
                  <div>
                    <input
                      type="text"
                      value={formData.zip}
                      onChange={(e) => handleInputChange("zip", e.target.value)}
                      placeholder="Irányítószám *"
                      style={inputStyle(formErrors.zip)}
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange("city", e.target.value)}
                      placeholder="Település *"
                      style={inputStyle(formErrors.city)}
                    />
                  </div>
                </div>
                <input
                  type="text"
                  value={formData.street}
                  onChange={(e) => handleInputChange("street", e.target.value)}
                  placeholder="Utca, házszám, emelet/ajtó *"
                  style={{ ...inputStyle(formErrors.street), marginBottom: 8 }}
                />
                <input
                  type="text"
                  value={formData.comment}
                  onChange={(e) => handleInputChange("comment", e.target.value)}
                  placeholder="Megjegyzés a futárnak (pl. kapucsengő)"
                  style={inputStyle(false)}
                />
              </div>

              {/* Számlázási cím megegyezik */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem", fontWeight: 700, color: isLandscape ? "#fff" : "#2d3a4a", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={formData.sameAsBilling}
                    onChange={(e) => handleInputChange("sameAsBilling", e.target.checked)}
                  />
                  A számlázási cím megegyezik a szállítási címmel
                </label>

                {!formData.sameAsBilling && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6, background: isLandscape ? "rgba(10,15,35,0.7)" : "#f8fafc", padding: 10, borderRadius: 10, border: isLandscape ? "1px solid rgba(255,255,255,0.15)" : "1px solid #e2e8f0" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                      <input
                        type="text"
                        value={formData.billingLastName}
                        onChange={(e) => handleInputChange("billingLastName", e.target.value)}
                        placeholder="Számlázási Családnév / Cégnév *"
                        style={inputStyle(formErrors.billingLastName)}
                      />
                      <input
                        type="text"
                        value={formData.billingFirstName}
                        onChange={(e) => handleInputChange("billingFirstName", e.target.value)}
                        placeholder="Számlázási Keresztnév"
                        style={inputStyle(false)}
                      />
                    </div>
                    <input
                      type="text"
                      value={formData.billingTaxNumber}
                      onChange={(e) => handleInputChange("billingTaxNumber", e.target.value)}
                      placeholder="Adószám (cég esetén)"
                      style={inputStyle(false)}
                    />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 6 }}>
                      <input
                        type="text"
                        value={formData.billingZip}
                        onChange={(e) => handleInputChange("billingZip", e.target.value)}
                        placeholder="Irányítószám *"
                        style={inputStyle(formErrors.billingZip)}
                      />
                      <input
                        type="text"
                        value={formData.billingCity}
                        onChange={(e) => handleInputChange("billingCity", e.target.value)}
                        placeholder="Település *"
                        style={inputStyle(formErrors.billingCity)}
                      />
                    </div>
                    <input
                      type="text"
                      value={formData.billingStreet}
                      onChange={(e) => handleInputChange("billingStreet", e.target.value)}
                      placeholder="Számlázási utca, házszám *"
                      style={inputStyle(formErrors.billingStreet)}
                    />
                  </div>
                )}
              </div>

              {/* Szállítási mód és Stripe bankkártyás fizetés */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: "0.75rem", fontWeight: 700, color: isLandscape ? "#ffcc00" : "#64748b", display: "block", marginBottom: 3 }}>Szállítás:</label>
                  <select
                    value={shippingMethod}
                    onChange={(e) => setShippingMethod(e.target.value)}
                    style={{ ...inputStyle(false), padding: 8 }}
                  >
                    <option value="gls_home">Futár (1 490 Ft)</option>
                    <option value="foxpost_box">Automata (990 Ft)</option>
                    <option value="personal">Személyes (0 Ft)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: "0.75rem", fontWeight: 700, color: isLandscape ? "#ffcc00" : "#64748b", display: "block", marginBottom: 3 }}>Fizetés:</label>
                  <div style={{ ...inputStyle(false), padding: 8, minHeight: 38, display: "flex", alignItems: "center", boxSizing: "border-box" }}>
                    Bankkártya a Stripe Checkoutban
                  </div>
                </div>
              </div>

              {/* GDPR & ÁSZF */}
              <div style={{ background: isLandscape ? "rgba(255,204,0,0.12)" : "#fef9c3", padding: 10, borderRadius: 10, fontSize: "0.8rem", color: isLandscape ? "#ffcc00" : "#713f12", marginBottom: 16 }}>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer", marginBottom: 4 }}>
                  <input type="checkbox" checked={formData.acceptTerms} onChange={(e) => handleInputChange("acceptTerms", e.target.checked)} />
                  <span>Elfogadom az ÁSZF-et *</span>
                </label>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                  <input type="checkbox" checked={formData.acceptGdpr} onChange={(e) => handleInputChange("acceptGdpr", e.target.checked)} />
                  <span>Hozzájárulok a GDPR adatkezeléshez *</span>
                </label>
              </div>

              {/* Végösszeg */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, borderTop: isLandscape ? "1px solid rgba(255,204,0,0.3)" : "1px solid #e2e8f0", paddingTop: 10 }}>
                <span style={{ fontWeight: 800, color: isLandscape ? "#fff" : "#2d3a4a" }}>Végösszeg:</span>
                <span style={{ fontSize: "1.35rem", fontWeight: 900, color: isLandscape ? "#ffcc00" : "#2563eb" }}>{grandTotal.toLocaleString()} Ft</span>
              </div>
              {checkoutMessage && <div role="alert" style={{ color: "#dc2626", marginBottom: 12, fontSize: "0.9rem" }}>{checkoutMessage}</div>}

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  onClick={() => setStep(2)}
                  style={{ background: isLandscape ? "rgba(30,35,60,0.8)" : "#f1f5f9", border: isLandscape ? "1px solid #4f8cff" : "1px solid #cbd5e1", borderRadius: 14, padding: "10px 16px", fontWeight: 600, color: isLandscape ? "#fff" : "#475569", cursor: "pointer" }}
                >
                  ← Vissza
                </button>
                <button
                  onClick={validateAndSubmit}
                  disabled={isSubmitting}
                  style={{
                    ...nextBtnStyle,
                    background: isLandscape
                      ? "linear-gradient(90deg, #10b981 0%, #059669 100%)"
                      : "linear-gradient(90deg, #10b981 0%, #059669 100%)",
                    border: isLandscape ? "2px solid #10b981" : "none",
                    boxShadow: "0 4px 16px rgba(16,185,129,0.5)",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  {isSubmitting ? "Fizetés előkészítése..." : "🔒 Tovább a Stripe-fizetéshez"}
                </button>
              </div>
            </div>
          )}

          {/* ==================== 4. LÉPÉS: SIKERES RENDELÉS ==================== */}
          {step === 4 && (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ fontSize: "3.5rem", marginBottom: 8 }}>🎉</div>
              <h3 style={{ margin: "0 0 8px 0", color: "#10b981", fontSize: "1.5rem", fontWeight: 800 }}>
                Sikeres Megrendelés!
              </h3>
              <p style={{ margin: "0 0 16px 0", color: isLandscape ? "#94a3b8" : "#64748b", fontSize: "0.9rem" }}>
                A Stripe megerősítette a fizetést. Köszönjük a rendelést!
              </p>

              <div style={{ background: isLandscape ? "rgba(10,15,35,0.7)" : "#f8fafc", padding: 16, borderRadius: 16, border: isLandscape ? "1px solid rgba(255,204,0,0.3)" : "1px solid #e2e8f0", textAlign: "left", marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ color: isLandscape ? "#94a3b8" : "#64748b", fontWeight: 600 }}>Rendelésszám:</span>
                  <span style={{ fontWeight: 800, color: isLandscape ? "#ffcc00" : "#2563eb" }}>#{orderNumber}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ color: isLandscape ? "#94a3b8" : "#64748b" }}>Méret:</span>
                  <span style={{ fontWeight: 700 }}>{currentSize.dimensions}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ color: isLandscape ? "#94a3b8" : "#64748b" }}>Mennyiség:</span>
                  <span style={{ fontWeight: 700 }}>{quantity} db</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ color: isLandscape ? "#94a3b8" : "#64748b" }}>Címzett:</span>
                  <span style={{ fontWeight: 700 }}>{formData.lastName} {formData.firstName}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 8, marginTop: 8 }}>
                  <span style={{ fontWeight: 800, color: isLandscape ? "#fff" : "#2d3a4a" }}>Összesen fizetendő:</span>
                  <span style={{ fontWeight: 900, color: "#10b981", fontSize: "1.15rem" }}>{grandTotal.toLocaleString()} Ft</span>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                <button
                  onClick={() => {
                    setStep(1);
                    setImageSrc(null);
                    setCroppedImage(null);
                    setQuantity(1);
                  }}
                  style={{ background: isLandscape ? "rgba(30,35,60,0.8)" : "#f1f5f9", border: isLandscape ? "1px solid #4f8cff" : "1px solid #cbd5e1", borderRadius: 12, padding: "10px 16px", fontWeight: 700, color: isLandscape ? "#fff" : "#475569", cursor: "pointer" }}
                >
                  ➕ Újabb mágnes
                </button>
                <button onClick={onBack} style={nextBtnStyle}>
                  🏠 Főmenü
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <AppFooter />
    </div>
  );
}
